import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WSService } from './ws.service';
import { NoticeSeverity, SystemStatusService } from './system-status.service';

/**
 * Servisne poruke (operater → restorani) + automatski incidenti o padu integracija.
 * Sluša WS poruku `service.message` (server već gejtuje po usluzi/zemlji/aplikaciji i šalje CEO
 * aktivan skup za ovu konekciju) i puni postojeći sistemski info-bar (SystemStatusService).
 *
 * Klijent: zatvaranje banera (× u baru) pamti se u localStorage da poruka ostane zatvorena i posle
 * reload-a; kad poruka više nije aktivna (deaktivirana/istekla) skida se iz bara, a njen ID se uklanja
 * iz "dismissed" liste (da ponovo objavljena poruka opet može da se prikaže).
 *
 * Auto-incidenti nose `translationKey` (+ params) pa se lokalizuju; ručne poruke koriste title/body.
 */
@Injectable({ providedIn: 'root' })
export class ServiceMessageMonitorService {
  private static readonly KEY_PREFIX = 'svcmsg:';
  private static readonly STORAGE_KEY = 'emeni.serviceMessages.dismissed';

  private dismissed = new Set<string>();
  private currentKeys = new Set<string>();

  constructor(ws: WSService, private status: SystemStatusService, private translate: TranslateService) {
    this.dismissed = this.loadDismissed();

    // Korisnik zatvorio baner → zapamti ID da se ne vraća (do deaktivacije/isteka).
    this.status.dismissed$.subscribe((key) => {
      if (!key.startsWith(ServiceMessageMonitorService.KEY_PREFIX)) {
        return;
      }
      const id = key.substring(ServiceMessageMonitorService.KEY_PREFIX.length);
      this.dismissed.add(id);
      this.saveDismissed();
      this.currentKeys.delete(key);
    });

    ws.observe('service.message').subscribe((msg) => {
      const messages: any[] = msg?.data?.messages ?? [];
      this.sync(messages);
    });
  }

  private sync(messages: any[]): void {
    const activeIds = new Set<string>(messages.map((m) => String(m.id)));

    // Očisti "dismissed" ID-jeve koji više nisu aktivni — da ponovo objavljena poruka opet vidi.
    let changed = false;
    for (const id of Array.from(this.dismissed)) {
      if (!activeIds.has(id)) {
        this.dismissed.delete(id);
        changed = true;
      }
    }
    if (changed) {
      this.saveDismissed();
    }

    const nextKeys = new Set<string>();
    for (const m of messages) {
      const id = String(m.id);
      if (this.dismissed.has(id)) {
        continue; // korisnik ga je zatvorio
      }
      const text = this.resolveText(m);
      if (!text) {
        continue;
      }
      const key = ServiceMessageMonitorService.KEY_PREFIX + id;
      this.status.set(key, this.mapSeverity(m.severity), text, true);
      nextKeys.add(key);
    }

    // Skini iz bara poruke koje su nestale iz aktivnog skupa (deaktivirane/istekle).
    for (const key of Array.from(this.currentKeys)) {
      if (!nextKeys.has(key)) {
        this.status.clear(key);
      }
    }
    this.currentKeys = nextKeys;
  }

  private resolveText(m: any): string {
    if (m.translationKey) {
      return this.translate.instant(m.translationKey, m.translationParams || {});
    }
    return [m.title, m.body].filter((s) => !!s && String(s).trim().length > 0).join(' — ');
  }

  /** Severity stiže kao string ('Info'|'Warning'|'Critical') preko StringEnumConverter — case-insensitive. */
  private mapSeverity(severity: any): NoticeSeverity {
    switch (String(severity).toLowerCase()) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warn';
      default:
        return 'info';
    }
  }

  private loadDismissed(): Set<string> {
    try {
      const raw = localStorage.getItem(ServiceMessageMonitorService.STORAGE_KEY);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  }

  private saveDismissed(): void {
    try {
      localStorage.setItem(
        ServiceMessageMonitorService.STORAGE_KEY,
        JSON.stringify(Array.from(this.dismissed)),
      );
    } catch {
      // localStorage nedostupan (privatni mod) — dismiss neće preživeti reload, ali baner i dalje radi.
    }
  }
}
